import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useChat } from '../components/chat/ChatContext';
import { useChatChannels } from '../components/chat/useChatChannels';
import ChannelList from '../components/chat/ChannelList';
import ChannelHeader from '../components/chat/ChannelHeader';
import MessageThread from '../components/chat/MessageThread';
import ThreadPanel from '../components/chat/ThreadPanel';
import ChatPageHeader from '../components/chat/ChatPageHeader';
import Sidebar from '../components/layout/Sidebar';
import { Icon } from '../components/ui';

export default function Chat() {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const { selectedChannelId, setSelectedChannelId, openChat, activeThreadId, closeThread } = useChat();
  const { channels } = useChatChannels();
  const [isMobile, setIsMobile] = useState(false);
  const [showChannelList, setShowChannelList] = useState(!channelId);

  // Track mobile breakpoint
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // With the keyboard up, iOS keeps the layout viewport full height, so a
  // page sized to 100dvh leaves the composer under the keyboard and the
  // whole page panning around. While a keyboard covers the screen, size the
  // page to the visible area instead (the bottom nav is behind the keyboard).
  const [keyboardViewport, setKeyboardViewport] = useState<number | null>(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || !isMobile) {
      setKeyboardViewport(null);
      return;
    }
    const onViewport = () => {
      const covered = window.innerHeight - vv.height;
      if (covered > 120) {
        setKeyboardViewport(Math.round(vv.height));
        window.scrollTo(0, 0);
      } else {
        setKeyboardViewport(null);
      }
    };
    onViewport();
    vv.addEventListener('resize', onViewport);
    vv.addEventListener('scroll', onViewport);
    return () => {
      vv.removeEventListener('resize', onViewport);
      vv.removeEventListener('scroll', onViewport);
    };
  }, [isMobile]);
  const fillStyle: React.CSSProperties | undefined =
    keyboardViewport !== null ? { height: `calc(${keyboardViewport}px - var(--sat))` } : undefined;

  // Sync URL → context
  useEffect(() => {
    if (channelId) {
      setSelectedChannelId(channelId);
      if (isMobile) setShowChannelList(false);
    }
  }, [channelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync context → URL (when user clicks channel in list)
  useEffect(() => {
    if (selectedChannelId && selectedChannelId !== channelId) {
      navigate(`/chat/${selectedChannelId}`, { replace: true });
      if (isMobile) setShowChannelList(false);
    }
  }, [selectedChannelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedChannel = channels.find(ch => ch.id === selectedChannelId);

  const handleBack = () => {
    setShowChannelList(true);
    setSelectedChannelId(null);
    navigate('/chat', { replace: true });
  };

  const handleMinimize = () => {
    openChat();
    navigate('/clients');
  };

  const workspaceStyle: React.CSSProperties = {
    background: 'var(--surface-card)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-rest)',
    overflow: 'hidden',
  };

  return (
    <div className="ck-fill-screen" style={fillStyle}>
      <Sidebar />
      <main className="px-4 md:px-8 md:pb-6 md:ml-[72px] ck-fill-screen flex flex-col" style={fillStyle}>
        {!isMobile ? (
          /* Desktop: header + workspace card (channel list | messages | thread) */
          <>
            <ChatPageHeader
              onSearchSelect={(channelId) => {
                setSelectedChannelId(channelId);
                navigate(`/chat/${channelId}`, { replace: true });
              }}
            />
            <div className="flex flex-1 min-h-0" style={workspaceStyle}>
              {/* Channel list */}
              <div
                className="w-[240px] flex-shrink-0 h-full"
                style={{ borderRight: '1px solid var(--border-hairline)' }}
              >
                <ChannelList />
              </div>

              {/* Message area */}
              <div className="flex flex-1 min-w-0 h-full">
                {/* Main chat area */}
                <div className="flex-1 flex flex-col min-w-0 h-full relative">
                  {selectedChannel ? (
                    <>
                      <ChannelHeader
                        channel={selectedChannel}
                        onClose={handleMinimize}
                        mode="fullscreen"
                      />
                      <MessageThread channelId={selectedChannel.id} />
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Icon
                        name="messages-square"
                        size={40}
                        className="mb-4"
                        style={{ color: 'var(--text-faint)', opacity: 0.5 }}
                      />
                      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                        Select a channel to start chatting
                      </p>
                    </div>
                  )}
                </div>

                {/* Thread panel */}
                <AnimatePresence>
                  {activeThreadId && (
                    <motion.div
                      initial={{ x: '100%', opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: '100%', opacity: 0 }}
                      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                      className="w-[340px] flex-shrink-0 h-full"
                    >
                      <ThreadPanel threadId={activeThreadId} onClose={closeThread} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </>
        ) : (
          /* Mobile: list or messages, not both */
          <div className={`flex flex-col flex-1 overflow-hidden ${keyboardViewport !== null ? 'pt-1' : 'pt-4'}`}>
            {showChannelList || (!activeThreadId && !selectedChannel) ? (
              <div className="flex flex-col flex-1 min-h-0" style={workspaceStyle}>
                <div
                  className="flex items-center px-4 py-3 flex-shrink-0"
                  style={{ borderBottom: '1px solid var(--border-hairline)' }}
                >
                  <h1
                    style={{
                      margin: 0,
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                      letterSpacing: 'var(--tracking-tight)',
                      color: 'var(--text-body)',
                    }}
                  >
                    Chat
                  </h1>
                </div>
                <ChannelList />
              </div>
            ) : activeThreadId ? (
              <div
                className="flex flex-col flex-1 relative min-h-0"
                style={workspaceStyle}
              >
                <ThreadPanel threadId={activeThreadId} onClose={closeThread} />
              </div>
            ) : selectedChannel ? (
              <div
                className="flex flex-col flex-1 relative min-h-0"
                style={workspaceStyle}
              >
                <ChannelHeader
                  channel={selectedChannel}
                  onClose={handleMinimize}
                  onBack={handleBack}
                  isMobile
                  mode="fullscreen"
                />
                <MessageThread channelId={selectedChannel.id} />
              </div>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}
